import { TFunction } from 'i18next';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { CompanyAdmin } from '../../../../../../typings/api/skymap/rest/v0/company';
import { formattedDateTime } from '../../../../utils/dateUtils';
import { sessionStorageUtils } from '../../../../utils/session-storage-utils';
import { isDefined } from '../../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../hooks/use-dialog';
import { useCompany, useCompanyState } from '../../../state/company-state';
import { useOptionalUserState } from '../../../state/user-state/user-state';
import { Button } from '../../button/button';
import { ConfirmationDialog } from '../../confirmation-dialog/confirmation-dialog';
import { DataTable, DataTableColumns, DataTableSortType } from '../../data-table/data-table';
import { Icon } from '../../icon/icon';
import { InviteCompanyAdminDialog } from '../../invite-company-admin/invite-company-admin-dialog';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';

const pageSize = 15;

export interface CompanyAdminTableView extends Record<string, unknown> {
  name: string;
  userId: string;
  email: string;
  createdAt: Date;
  createdAtText: string;
  deleteIcon: ReactNode;
}

function getColumns(t: TFunction): DataTableColumns<CompanyAdminTableView> {
  const columns: DataTableColumns<CompanyAdminTableView> = {
    name: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('name', { ns: 'common' }),
      unfilterable: true,
    },
    email: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('newCompanyAdmin.newUser.form.email', { ns: 'components' }),
      unfilterable: true,
    },
    createdAt: {
      sortableBy: { type: DataTableSortType.DATE },
      title: t('licenses.createdAt', { ns: 'companies' }),
      formatter: (value: unknown) => formattedDateTime(value as Date),
      unfilterable: true,
    },
  };

  columns.deleteIcon = {
    alignment: 'right',
    unfilterable: true,
    width: '40px',
  };

  return columns;
}

function DeleteCompanyAdminIcon(userId: string, t: TFunction, currentUserId: string | undefined) {
  const isCurrentUserKnown = Boolean(currentUserId);
  const isLoggedInUser = isCurrentUserKnown && userId === currentUserId;
  return (
    <Icon
      disabled={isLoggedInUser}
      fixedWidth={true}
      icon={['fad', 'trash-alt']}
      title={
        isLoggedInUser
          ? t('companyAdmins.notPossibleToRemoveYourself', { ns: 'companies' })
          : undefined
      }
      onHoverStyle={{ icon: ['fas', 'trash-alt'] }}
    />
  );
}

const createTableView = (
  admins: CompanyAdmin[],
  t: TFunction,
  currentUserId: string | undefined,
): CompanyAdminTableView[] =>
  admins.map((admin) => ({
    name: admin.name,
    userId: admin.userId,
    email: admin.email,
    createdAt: new Date(admin.createdAt),
    createdAtText: formattedDateTime(admin.createdAt),
    deleteIcon: DeleteCompanyAdminIcon(admin.userId, t, currentUserId),
  }));

const searchColumns = ['type', 'status', 'belongsTo', 'createdAtText', 'validUntilText'] as const;

function filterItems(items: CompanyAdminTableView[], searchText: string) {
  return items.filter((item) => {
    const searchValue = searchText.trim().toLowerCase();
    if (searchValue.length === 0) {
      return true;
    }

    return searchColumns.some((field) => {
      const candidate = item[field];

      if (typeof candidate !== 'string') {
        return false;
      }

      return candidate.toLowerCase().includes(searchValue);
    });
  });
}

/**
 * Hook that preserves text in search during the user session.
 */
function useCompanyAdminSearchFilter() {
  const [searchText, setSearchText] = useState(
    sessionStorageUtils.getUserItemOrDefault('companyAdminListSearchText', ''),
  );

  useEffect(() => {
    sessionStorageUtils.setUserItem('companyAdminListSearchText', searchText);
  }, [searchText]);

  return { searchText, setSearchText };
}

type CompanyAdminsProps = {
  showTitle?: boolean;
};

export const CompanyAdmins = ({ showTitle = true }: CompanyAdminsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const userState = useOptionalUserState();
  const currentUserId = userState?.user?.id;
  const isOrganizationAdmin = userState?.isOrganizationAdmin ?? false;
  const company = useCompany();
  const { removeCompanyAdmin } = useCompanyState();
  const deleteAdminDialog = useDialog();
  const inviteAdminDialog = useDialog();
  const [selectedItem, setSelectedItem] = useState<CompanyAdminTableView>();

  const [fullTableView, setFullTableView] = useState(
    createTableView(company.companyAdmins, t, currentUserId),
  );
  const [tableView, setTableView] = useState<CompanyAdminTableView[]>(fullTableView);
  const { searchText, setSearchText } = useCompanyAdminSearchFilter();

  const columns = useRef(getColumns(t));

  useEffect(() => {
    setFullTableView(createTableView(company.companyAdmins, t, currentUserId));
  }, [company.companyAdmins, currentUserId, t]);

  useEffect(() => {
    const items = filterItems(fullTableView, searchText);
    setTableView(items);
  }, [searchText, theme, fullTableView]);

  return (
    <>
      {showTitle && (
        <FallbackTitle>
          {t('menu.companyAdmins.title', { ns: 'navigation' })} - {company.name}
        </FallbackTitle>
      )}
      <Stack alignItems="baseline" direction="row" justifyContent="space-between" spacing={0.5}>
        <SearchFilterContainer direction="row" spacing={0.5}>
          <TextBox
            placeholder={t('search', { ns: 'common' })}
            value={searchText}
            width={250}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </SearchFilterContainer>
        <Button color="primary" variant="contained" onClick={() => inviteAdminDialog.show()}>
          {t('companyAdmins.inviteCompanyAdmin.title', { ns: 'companies' })}
        </Button>
      </Stack>

      <DataTable
        columns={columns.current}
        fixedLayout={false}
        items={tableView}
        pageSize={pageSize}
        tableStyle={{
          rowCell: {
            fontSize: '14px',
          },
          headerCell: {
            fontSize: '14px',
          },
        }}
        onRowClicked={(item) => {
          const canOpenDeleteDialog =
            isOrganizationAdmin || (Boolean(currentUserId) && item.userId !== currentUserId);
          if (canOpenDeleteDialog) {
            setSelectedItem(item);
            deleteAdminDialog.show();
          }
        }}
      />

      {inviteAdminDialog.render(<InviteCompanyAdminDialog dialog={inviteAdminDialog} />)}

      {isDefined(selectedItem) &&
        deleteAdminDialog.render(
          <ConfirmationDialog
            maxWidth={500}
            negative={true}
            title={t('companyAdmins.removeRole', { ns: 'companies' })}
            onCancel={deleteAdminDialog.hide}
            onConfirm={async () => {
              const userId = selectedItem.userId;

              await removeCompanyAdmin(userId);

              setSelectedItem(undefined);

              publish(SubscriptionTopic.ToastrMessage, {
                type: 'success',
                message: t('companyAdmins.roleRemoved', { ns: 'companies' }),
              });
              deleteAdminDialog.hide();
            }}
          >
            <span>
              <Trans
                components={{
                  bold: <strong />,
                }}
                i18nKey="companyAdmins.confirmRemoval"
                ns="companies"
                values={{
                  userName: selectedItem.name,
                }}
              />
            </span>
          </ConfirmationDialog>,
        )}
    </>
  );
};

// Styling is made to match the other pages in angular.
const FallbackTitle = styled.h1`
  font-size: 19px;
  font-weight: bold;
  text-transform: uppercase;
`;

const SearchFilterContainer = styled(Stack)`
  width: 450px;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;
